<template>
  <v-app>
    <v-container fluid>
      <v-card variant="outlined" class="production-list-card">
        <v-card-title class="d-flex flex-wrap align-center ga-2 bg-blue-grey-lighten-4 text-subtitle-1">
          <span>Production Completion List</span>
          <v-btn size="small" variant="tonal" color="primary" prepend-icon="mdi-chart-box-outline" :to="{ name: 'production-reports' }">Reports Center</v-btn>
          <v-spacer />
          <v-text-field
            v-model="search"
            label="Search"
            prepend-inner-icon="mdi-magnify"
            density="compact"
            variant="outlined"
            hide-details
            clearable
            style="max-width: 280px"
          />
          <v-btn color="primary" prepend-icon="mdi-plus" :to="{ name: 'create-production' }">
            Complete Production
          </v-btn>
        </v-card-title>

        <div class="production-list-table-wrap">
          <v-data-table
            :headers="headers"
            :items="items"
            :search="search"
            :loading="loading"
            density="compact"
            class="production-list-table"
          >
            <template #item.index="{ index }">{{ index + 1 }}</template>
            <template #item.production_date="{ item }">{{ formattedDate(item.production_date) }}</template>

            <template #item.output="{ item }">
              <div v-if="!item.details?.length" class="text-medium-emphasis">—</div>
              <div v-for="detail in item.details || []" :key="detail.id" class="output-line">
                <div class="font-weight-medium">{{ detail.product?.name || '—' }}</div>
                <div class="text-caption text-medium-emphasis">
                  {{ number(detail.planned_quantity) }} planned / {{ number(detail.actual_quantity ?? detail.quantity) }} actual
                </div>
              </div>
            </template>

            <template #item.finished_batch_no="{ item }">
              <div v-if="!item.details?.length" class="text-medium-emphasis">—</div>
              <div v-for="detail in item.details || []" :key="detail.id" class="output-line">
                <span class="batch-number">{{ detail.batch_no || '—' }}</span>
              </div>
            </template>

            <template #item.manufacturing_date="{ item }">
              <div v-if="!item.details?.length" class="text-medium-emphasis">—</div>
              <div v-for="detail in item.details || []" :key="detail.id" class="output-line date-line">
                {{ formattedDate(detail.manufacturing_date) }}
              </div>
            </template>

            <template #item.expiry_date="{ item }">
              <div v-if="!item.details?.length" class="text-medium-emphasis">—</div>
              <div v-for="detail in item.details || []" :key="detail.id" class="output-line date-line">
                {{ formattedDate(detail.expiry_date) }}
              </div>
            </template>

            <template #item.total_cost="{ item }">{{ number(item.total_cost, 2) }}</template>
            <template #item.workflow_status="{ item }">
              <v-chip size="small" :color="workflowColor(item.workflow_status)" variant="tonal">
                {{ labelize(item.workflow_status) }}
              </v-chip>
            </template>
            <template #item.approval_status="{ item }">
              <v-chip size="small" :color="approvalColor(item.approval_status)">
                {{ item.approval_status || (item.status ? 'Pending' : 'Draft') }}
              </v-chip>
            </template>
            <template #item.actions="{ item }">
              <v-btn icon="mdi-file-document-outline" color="blue" variant="text" size="small" :to="{ name: 'production-details', params: { id: item.id } }" />
              <v-btn
                v-if="canEdit(item) && $updateAccess($route.path)"
                icon="mdi-pencil-outline"
                color="primary"
                variant="text"
                size="small"
                :to="{ name: 'create-production', params: { id: item.id } }"
              />
            </template>
          </v-data-table>
        </div>
      </v-card>
    </v-container>
  </v-app>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";

const store = useStore();
const search = ref("");
const loading = ref(false);
const items = computed(() => store.getters.getPrdProductionList || []);

const headers = [
  { title: "#", key: "index", sortable: false, width: "45px" },
  { title: "Code", key: "code", width: "170px" },
  { title: "Production Date", key: "production_date", width: "125px" },
  { title: "Finished Product (Planned / Actual)", key: "output", sortable: false, width: "250px" },
  { title: "FG Lot / Batch No.", key: "finished_batch_no", sortable: false, width: "200px" },
  { title: "Manufacturing Date", key: "manufacturing_date", sortable: false, width: "140px" },
  { title: "Expiry Date", key: "expiry_date", sortable: false, width: "125px" },
  { title: "Total Cost", key: "total_cost", align: "end", width: "110px" },
  { title: "Workflow", key: "workflow_status", width: "200px" },
  { title: "Approval", key: "approval_status", width: "100px" },
  { title: "Actions", key: "actions", sortable: false, width: "100px" },
];

function number(value, digits = 4) {
  return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: digits });
}

// Laravel date casts may serialize local midnight as the previous day in UTC.
// Display full ISO timestamps in the ERP's Bangladesh calendar date; date-only
// strings are formatted directly so they are never shifted by the browser TZ.
function formattedDate(value) {
  if (!value) return "—";

  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [year, month, day] = raw.split("-");
    return `${day}/${month}/${year}`;
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime())
    ? "—"
    : parsed.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Asia/Dhaka" });
}

function labelize(value) {
  return String(value || "Draft").replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function approvalColor(status) {
  if (status === "Approved") return "success";
  if (status === "Cancelled" || status === "Rejected") return "error";
  return "warning";
}

function workflowColor(status) {
  if (["inventory_received", "completed"].includes(status)) return "success";
  if (["completed_pending_inventory", "sent_to_inventory", "partially_sent_to_inventory"].includes(status)) return "indigo";
  return "warning";
}

function canEdit(item) {
  return !item.posted_at && !item.approval_current_process && (!item.approval_status || item.approval_status === "Pending") && Number(item.status) === 0;
}

onMounted(async () => {
  loading.value = true;
  try {
    await store.dispatch("getPrdProductionList");
  } catch (error) {
    toast.error(error?.response?.data?.message || "Production list could not be loaded.");
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.production-list-card { overflow: hidden; }
.production-list-table-wrap { overflow-x: auto; }
.production-list-table { min-width: 1550px; }
.production-list-table :deep(th) { white-space: nowrap; font-size: 12px; }
.production-list-table :deep(td) { vertical-align: top; padding-top: 9px !important; padding-bottom: 9px !important; }
.output-line { min-height: 43px; padding: 4px 0; border-bottom: 1px solid rgba(0,0,0,.06); }
.output-line:last-child { border-bottom: 0; }
.date-line { display: flex; align-items: center; white-space: nowrap; }
.batch-number { display: inline-block; overflow-wrap: anywhere; font-weight: 600; color: #225d9f; }
@media (max-width: 960px) {
  .production-list-table { min-width: 1380px; }
}
</style>
